import React, { useState, useEffect, useContext } from 'react';
import {
  Grid,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Typography,
  Box,
} from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';

import { useSelector } from 'react-redux';
import { AdvancedMdContext } from '../../../../context/AdvancedMD/AdvancedMdContext';

import { buildHedisContextText } from '../../../../utils/clinicalNotesFormatter';
import { generateClinicalAiPanelsFromAI } from '../../../../utils/services/clinicalAiPanelsService';
import { SECTION_KEYS } from '../../models/clinicalAiModels';

import AsthmaSection from './sections/AsthmaSection';
import COPDSection from './sections/COPDSection';
import HeartFailureSection from './sections/HeartFailureSection';
import AFSection from './sections/AFSection';
import HypertensionSection from './sections/HypertensionSection';
import CVRiskSection from './sections/CVRiskSection';

/**
 * Self-contained: obtiene datos de AdvancedMD context + Redux,
 * construye contexto clínico completo y llama a la IA on-demand.
 *
 * @param {{ patientSummary: Object }} props
 */
const ClinicalAiPanels = ({ patientSummary }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sectionsSummary, setSectionsSummary] = useState(null);
  const [sectionsData, setSectionsData] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [diagnoses, setDiagnoses] = useState([]);

  // AdvancedMD ID desde Redux
  const { advancedMdPatientId } = useSelector(
    (state) => state.patientMapSelect
  );

  // Contexto AdvancedMD para meds, labs, allergies + notas clínicas
  const {
    medications,
    medicationsLoading,
    loadMedications,
    labs,
    labsLoading,
    loadLabs,
    allergies,
    allergiesLoading,
    loadAllergies,
    annualWellnessNotes,
    followUpExamNotes,
    telehealthNotes,
    hospitalDischargeNotes,
  } = useContext(AdvancedMdContext);

  // Cargar meds / labs / allergies desde AdvancedMD
  useEffect(() => {
    if (!advancedMdPatientId) return;
    loadMedications(advancedMdPatientId);
    loadLabs(advancedMdPatientId);
    loadAllergies(advancedMdPatientId);
  }, [advancedMdPatientId, loadMedications, loadLabs, loadAllergies]);

  const anyBaseLoading = medicationsLoading || labsLoading || allergiesLoading;

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);

    try {
      const contextText = buildHedisContextText({
        demographics: patientSummary || {},
        annualWellnessNotes: annualWellnessNotes || [],
        followUpExamNotes: followUpExamNotes || [],
        telehealthNotes: telehealthNotes || [],
        hospitalDischargeNotes: hospitalDischargeNotes || [],
        medications: Array.isArray(medications) ? medications : [],
        labs: Array.isArray(labs) ? labs : [],
        allergies: Array.isArray(allergies) ? allergies : [],
      });

      if (!contextText.trim()) {
        setError('No clinical data available for condition analysis.');
        setSectionsSummary(null);
        setSectionsData({});
        return;
      }

      const resp = await generateClinicalAiPanelsFromAI({ contextText });

      setDiagnoses(resp.diagnoses || []);
      setSectionsSummary(resp.sectionsSummary || {});
      setSectionsData(resp.sectionsData || {});
    } catch (err) {
      console.error('[ClinicalAiPanels] Error generating panels:', err);
      setError('Error generating condition analysis with AI.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={2}>
      {/* Generate button */}
      <Grid item xs={12}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="subtitle1" fontWeight={900}>
            Specific Condition Analysis
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            {anyBaseLoading && (
              <Typography variant="caption" color="text.secondary">
                Loading clinical data...
              </Typography>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={handleGenerate}
              disabled={loading || anyBaseLoading}
              startIcon={
                loading ? <CircularProgress size={16} /> : <PsychologyIcon />
              }
            >
              {loading
                ? 'Generating...'
                : 'Generate Condition Analysis with AI'}
            </Button>
          </Stack>
        </Stack>
      </Grid>

      {/* Loading state */}
      {loading && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 3,
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Generating condition panels with AI...
            </Typography>
          </Box>
        </Grid>
      )}

      {/* Error state */}
      {!loading && error && (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}

      {/* Empty state (before first generation) */}
      {!loading && !error && !sectionsSummary && (
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary">
            Click &quot;Generate Condition Analysis with AI&quot; to evaluate
            specific conditions for this patient based on their clinical data.
          </Typography>
        </Grid>
      )}

      {/* Section panels */}
      {!loading && sectionsSummary && (
        <>
          {sectionsSummary[SECTION_KEYS.HYPERTENSION] && (
            <Grid item xs={12} md={6}>
              <HypertensionSection data={sectionsData.hypertension} />
            </Grid>
          )}

          {sectionsSummary[SECTION_KEYS.CV_RISK] && (
            <Grid item xs={12} md={6}>
              <CVRiskSection data={sectionsData.cvRisk} />
            </Grid>
          )}

          {sectionsSummary[SECTION_KEYS.AF] && (
            <Grid item xs={12} md={12}>
              <AFSection data={sectionsData.af} />
            </Grid>
          )}

          {sectionsSummary[SECTION_KEYS.HEART_FAILURE] && (
            <Grid item xs={12} md={6}>
              <HeartFailureSection data={sectionsData.heartFailure} />
            </Grid>
          )}

          {sectionsSummary[SECTION_KEYS.COPD] && (
            <Grid item xs={12} md={6}>
              <COPDSection data={sectionsData.copd} />
            </Grid>
          )}

          {sectionsSummary[SECTION_KEYS.ASTHMA] && (
            <Grid item xs={12} md={6}>
              <AsthmaSection data={sectionsData.asthma} />
            </Grid>
          )}

          {/* No active sections message */}
          {!sectionsSummary[SECTION_KEYS.HYPERTENSION] &&
            !sectionsSummary[SECTION_KEYS.CV_RISK] &&
            !sectionsSummary[SECTION_KEYS.AF] &&
            !sectionsSummary[SECTION_KEYS.HEART_FAILURE] &&
            !sectionsSummary[SECTION_KEYS.COPD] &&
            !sectionsSummary[SECTION_KEYS.ASTHMA] && (
              <Grid item xs={12}>
                <Alert severity="info">
                  No specific conditions (hypertension, CV risk, AF, heart
                  failure, COPD, asthma) were identified in the clinical data.
                </Alert>
              </Grid>
            )}
        </>
      )}
    </Grid>
  );
};

export default ClinicalAiPanels;
